import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
// import { useTranslation } from "react-i18next";
import botAvatar from "@/assets/image.png";

// import { Button } from "@/components/ui/button";
// import { GroundingFiles } from "@/components/ui/grounding-files";
// import GroundingFileView from "@/components/ui/grounding-file-view";
// import StatusMessage from "@/components/ui/status-message";
import { BiSupport } from "react-icons/bi";
import Marquee from "react-fast-marquee";
import useRealTime from "@/hooks/useRealtime";
import useAudioRecorder from "@/hooks/useAudioRecorder";
import useAudioPlayer from "@/hooks/useAudioPlayer";

// import { GroundingFile, ToolResult } from "./types";
import { useAuth } from "./context/AuthContext";
import Header from "./Header";
import Avatar from "./components/ui/avatar";
import bgVideo from "./assets/bgVideo.mp4";
import Carousel from "./Carousel";
import axios from "axios";
function App() {
    const { user } = useAuth();
    if (!user?.isLoggedIn) {
        window.location.href = "https://account.drake-ltim.com/auth/lisa/signin";
        return "Redirecting";
    }

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isCalling, setIsCalling] = useState(false);
    const [speaking, setSpeaking] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [caption, setCaption] = useState("");
    const [convId, setConvId] = useState<number | null>(null);
    // const [groundingFiles, setGroundingFiles] = useState<GroundingFile[]>([]);
    // const [selectedFile, setSelectedFile] = useState<GroundingFile | null>(null);
    const appendConv = async (type: string, message: string) => {
        if (!convId || !callDuration) return;
        try {
            const url = import.meta.env.VITE_CALL_INSIGHTS_BACKEND + "/app/lisa/append-conv-message";
            await axios.post(url, {
                convId: convId,
                convMessage: message,
                messageType: type,
                callTime: callDuration
            });
        } catch (err) {
            console.error(err);
        }
    };

    const { startSession, endSession, addUserAudio, inputAudioBufferClear } = useRealTime({
        onWebSocketOpen: () => console.log("WebSocket connection opened"),
        onWebSocketClose: () => console.log("WebSocket connection closed"),
        onWebSocketError: event => console.error("WebSocket error:", event),
        onReceivedError: message => console.error("error", message),
        onReceivedResponseAudioDelta: message => {
            isRecording && playAudio(message.delta);
        },
        onReceivedInputAudioBufferSpeechStarted: () => {
            stopAudioPlayer();
        },
        onReceivedInputAudioTranscriptionCompleted: message => {
            console.log("user input message", message.transcript);
            setCaption(message.transcript);
            appendConv("human", message.transcript);
        },

        onReceivedResponseAudioTranscriptDone: message => {
            setCaption(message.transcript);
            appendConv("ai", message.transcript);
        }
        // onReceivedExtensionMiddleTierToolResponse: message => {
        //     const result: ToolResult = JSON.parse(message.tool_result);

        //     const files: GroundingFile[] = result.sources.map(x => {
        //         return { id: x.chunk_id, name: x.title, content: x.chunk };
        //     });

        //     // setGroundingFiles(prev => [...prev, ...files]);
        // }
    });

    const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();
    const { start: startAudioRecording, stop: stopAudioRecording, analyser, dataArray } = useAudioRecorder({ onAudioRecorded: addUserAudio });

    const onToggleListening = async () => {
        if (!isRecording) {
            await startAudioRecording();
            resetAudioPlayer();
            setIsRecording(true);
        } else {
            await stopAudioRecording();
            inputAudioBufferClear();
            setIsRecording(false);
        }
    };

    const onToggleCall = async () => {
        if (!isCalling) {
            setTimeout(async () => {
                try {
                    const id: any = await startSession();
                    if (id) setConvId(id);
                } catch (err) {}
            }, 200);
            await startAudioRecording();
            resetAudioPlayer();
            setIsCalling(true);
            setIsRecording(true);
        } else {
            if (convId) endSession(convId, callDuration);
            setCallDuration(0);
            await stopAudioRecording();
            stopAudioPlayer();
            inputAudioBufferClear();
            setIsRecording(false);
            setIsCalling(false);
            setCaption("");
        }
    };

    const drawBars = () => {
        if (!canvasRef.current || !analyser || !dataArray) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        if (!ctx) return;

        const width = canvas.width;
        const height = canvas.height;
        const centerY = height / 2; // Centerline of the canvas

        ctx.clearRect(0, 0, width, height);
        const gradient = ctx.createLinearGradient(0, 0, width, 0);
        gradient.addColorStop(0, "#9333ea"); // Tailwind purple 600
        gradient.addColorStop(1, "#db2777"); // Tailwind pink 600
        analyser.getByteFrequencyData(dataArray); // Get frequency data

        const barWidth = (width / analyser.frequencyBinCount) * 1.5; // Thinner bars
        let barHeight;
        let x = 0;

        for (let i = 0; i < analyser.frequencyBinCount; i++) {
            barHeight = dataArray[i];

            // Centering the bars vertically by drawing them both above and below the center line
            ctx.fillStyle = gradient;
            ctx.fillRect(x, centerY - barHeight / 4, barWidth, barHeight / 2);

            x += barWidth + 0.5; // Narrower spacing between bars
        }

        // Check if user is speaking based on frequency data
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setSpeaking(average > 128);
    };

    useEffect(() => {
        const interval = setInterval(drawBars, 50);
        return () => clearInterval(interval);
    }, [analyser, dataArray]);

    useEffect(() => {
        let timer: NodeJS.Timeout | undefined;
        if (isCalling) {
            timer = setInterval(() => {
                setCallDuration(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(timer); // Clear timer on unmount or end call
    }, [isCalling]);
    // const { t } = useTranslation();
    // console.log("user", user);
    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    };
    return (
        <>
            <video autoPlay loop muted className="fixed left-0 top-0 z-[-1] h-svh w-screen object-cover opacity-40">
                <source src={bgVideo} />
            </video>
            <div className="z-20 flex min-h-svh w-screen flex-col">
                <Header />
                <main className="container mx-auto flex flex-grow flex-col items-center justify-center gap-4 p-3 text-white lg:flex-row lg:justify-between 2xl:gap-8 2xl:p-4">
                    <div className="flex w-full flex-col lg:w-1/2">
                        <Carousel />
                        <div className="mt-8 flex w-full flex-col items-center justify-center gap-4 lg:mt-12 lg:flex-row lg:justify-start">
                            <button
                                onClick={onToggleCall}
                                disabled={isCalling}
                                className={`mx-auto flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-6 py-4 font-medium transition-colors duration-300 hover:bg-gray-900 lg:mx-0 ${isCalling && "opacity-40"}`}
                            >
                                <BiSupport /> <span>Connect Now</span>
                            </button>
                            <span>OR</span>
                            <a
                                href="tel:+16674010009"
                                className="bg-gradient-to-r from-rose-200 via-fuchsia-200 to-indigo-200 bg-clip-text font-extrabold text-transparent"
                            >
                                Call Us <span className="underline">+1-(667) 401-0009 </span>
                            </a>
                        </div>
                    </div>
                    {isCalling ? (
                        <div className="order-1 flex w-full flex-col items-center justify-center gap-4 rounded-3xl bg-gray-950/80 p-12 lg:order-2 lg:w-1/2 lg:gap-6 xl:p-8 2xl:gap-10 2xl:p-12">
                            <div className="flex flex-col items-center justify-center gap-2">
                                <h2 className="font-bold"> {formatTime(callDuration)}</h2>
                                <h2 className="font-medium">Call Ongoing </h2>
                            </div>
                            <canvas ref={canvasRef} className="h-[100px] w-full max-w-sm transition-all" />
                            <div className="flex w-full flex-wrap items-center justify-center gap-4 lg:flex-nowrap xl:gap-12">
                                <Avatar type="user" name={user?.name} speaking={speaking} className="lg:order-1" />
                                {/* show animation here */}
                                <Avatar type="bot" name="Lisa" className="lg:order-3" />
                            </div>
                            <div className={`h-6 w-full`}>
                                <Marquee delay={3} pauseOnHover speed={70} className="text-center text-sm">
                                    {caption && `CC: ${caption}....`}
                                </Marquee>
                            </div>
                            <div className="flex w-full items-center justify-center gap-4">
                                {isCalling && (
                                    <button
                                        onClick={onToggleListening}
                                        className={`flex items-center justify-center rounded-lg border border-gray-400 bg-transparent p-4 font-bold text-gray-200`}
                                    >
                                        {isRecording ? <Mic className="text-xl" /> : <MicOff className="text-xl" />}
                                    </button>
                                )}
                                <button
                                    onClick={onToggleCall}
                                    className={`w-60 rounded-lg border px-6 py-4 font-bold ${isCalling ? "border-red-600 bg-red-600 hover:border-red-700 hover:bg-red-700" : "border-green-600 bg-green-600 hover:border-green-700 hover:bg-green-700"}`}
                                    // aria-label={isRecording ? t("app.stopRecording") : t("app.startRecording")}
                                >
                                    {isCalling ? (
                                        <>
                                            End Call
                                            {/* <MicOff className="mr-2 h-4 w-4" /> */}
                                            {/* {t("app.stopConversation")} */}
                                        </>
                                    ) : (
                                        <>
                                            Start Call
                                            {/* <Mic className="mr-2 h-6 w-6" /> */}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <img src={botAvatar} className="hidden w-full max-w-lg rounded-3xl lg:block" />
                    )}

                    {/* <StatusMessage isRecording={isRecording} /> */}
                    {/* <GroundingFiles files={groundingFiles} onSelected={setSelectedFile} /> */}
                </main>

                {/* <GroundingFileView groundingFile={selectedFile} onClosed={() => setSelectedFile(null)} /> */}
                <footer className="bg-gray-950/60 p-1">
                    <p className="text-center text-xs text-gray-400">
                        Powered by Lisa
                    </p>
                </footer>
            </div>
        </>
    );
}

export default App;
